import { useEffect, useState } from "react"
import { Button } from "./ui/button"
import { PhoneIcon, CalendarIcon, HeartIcon, ClockIcon } from "lucide-react"

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="relative h-screen w-full overflow-hidden bg-gray-900">
      {/* Imagen de fondo con overlay */}
      <div className="absolute inset-0">
        <img
          src="/foto_clinica.png"
          alt="Clínica Cardiología del Norte"
          className="w-full h-full object-cover object-center"
        />

        {/* Gradient overlay para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/40 via-blue-800/30 to-purple-900/20"></div>

        {/* Overlay oscuro de arriba hacia abajo */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-transparent"></div>

        {/* Patrón adicional para efecto médico */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 35px,
              rgba(255, 255, 255, 0.05) 35px,
              rgba(255, 255, 255, 0.05) 70px
            )`
          }}></div>
        </div>
      </div>

      {/* Contenido principal - Flex container */}
      <div className="relative z-10 h-full flex flex-col pt-20 sm:pt-22 md:pt-28 lg:pt-32">
        {/* Sección de Texto - Flex-shrink-0 para que respete su espacio, pero más compacto */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex-shrink-0 z-20">
          <div
            className={`w-full text-center space-y-1 md:space-y-2 lg:space-y-3 transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Título principal - Ajuste de tamaños para dar espacio al doctor */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight whitespace-normal md:whitespace-nowrap text-center drop-shadow-lg">
              SOLUCIONES DE <span className="block sm:inline text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 drop-shadow-none filter brightness-110">SALUD CARDÍACA</span>
            </h1>

            {/* Subtítulo */}
            <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-gray-200 font-light italic drop-shadow-md">
              "Tu corazón es primero"
            </p>

            {/* Descripción */}
            <p className="text-gray-300 text-sm sm:text-base md:text-xl lg:text-2xl xl:text-3xl leading-relaxed max-w-4xl mx-auto drop-shadow-md">
              Expertos en cardiología con tecnología de vanguardia.
              <br className="hidden sm:block" />
              Cuidamos de tu salud cardiovascular con el más alto nivel de profesionalismo.
            </p>
          </div>
        </div>

        {/* Sección Visual del Doctor - Ocupa el espacio restante */}
        <div className="flex-1 min-h-0 relative w-full flex items-end justify-center mt-2 sm:mt-3 md:mt-2">

          {/* Doctor Container */}
          <div className={`relative h-full w-full flex items-end justify-center transition-all duration-1000 delay-300 ${
            isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}>

             {/* Círculo decorativo de fondo */}
             <div className="absolute inset-x-0 bottom-0 flex items-end justify-center pointer-events-none">
               <div className="w-[40vh] h-[40vh] sm:w-[45vh] sm:h-[45vh] bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl animate-pulse mb-10"></div>
             </div>

            {/* Imagen del doctor - Se adapta a la altura disponible */}
            <img
              src="/foto_doctor.png"
              alt="Dr. Ramón - Cardiólogo"
              className="relative z-10 w-auto h-full object-contain object-bottom drop-shadow-2xl scale-110 sm:scale-115 lg:scale-100 xl:scale-110 origin-bottom"
              style={{
                filter: "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))",
                maxHeight: "80vh"
              }}
            />
            
             {/* Botones - Posicionados sobre la imagen pero con cuidado */}
             <div className="absolute bottom-4 sm:bottom-8 md:bottom-12 lg:bottom-16 w-full z-30">
                <div className="container mx-auto px-4">
                  <div className="flex justify-center items-center gap-4 sm:gap-8 md:gap-24 lg:gap-80 xl:gap-[32rem]">
                    
                    {/* Botón izquierdo */}
                    <div className={`transition-all duration-1000 delay-500 ${
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                    }`}>
                      <Button
                        size="lg"
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl px-4 py-3 sm:px-8 sm:py-6 text-xs sm:text-base md:text-lg font-semibold rounded-full min-w-[120px] sm:min-w-[160px]"
                        asChild
                      >
                        <a href="#contacto" className="flex items-center justify-center">
                          <CalendarIcon className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-5 sm:w-5" />
                          <span>Agendar</span>
                          <span className="hidden sm:inline ml-1">Cita</span>
                        </a>
                      </Button>
                      <div className="mt-2 text-white text-[10px] sm:text-sm md:text-base text-center hidden sm:block">
                        <div className="flex items-center space-x-1 sm:space-x-2 justify-center">
                          <PhoneIcon className="h-3 w-3 sm:h-5 sm:w-5 text-cyan-400" />
                          <span className="font-medium">(81) 8343-2180</span>
                        </div>
                      </div>
                    </div>

                    {/* Botón derecho */}
                    <div className={`transition-all duration-1000 delay-500 ${
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
                    }`}>
                      <Button
                        size="lg"
                        variant="outline"
                        className="border border-white/40 sm:border-2 text-white hover:bg-white/10 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 px-4 py-3 sm:px-8 sm:py-6 text-xs sm:text-base md:text-lg font-semibold rounded-full min-w-[120px] sm:min-w-[160px]"
                        asChild
                      >
                        <a href="tel:+528183432180" className="flex items-center justify-center">
                          <PhoneIcon className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-5 sm:w-5" />
                          <span>Llamar</span>
                        </a>
                      </Button>
                      <div className="mt-2 text-white text-[10px] sm:text-sm md:text-base text-center hidden sm:block">
                        <div className="flex items-center space-x-1 sm:space-x-2 justify-center">
                          <ClockIcon className="h-3 w-3 sm:h-5 sm:w-5 text-cyan-400" />
                          <span className="font-medium">Lun-Vie: 8-20h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
             </div>

          </div>
        </div>
      </div>


      {/* Estilos de animación personalizados */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  )
}

export default HeroSection